/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAllBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandAlphabetLetterModel, BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem, Hidden, Radio, RadioGroup, Select, TextField } from "@insite/mobius/index";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@monsma/XDL/Components/Notify";
import { screen } from "@monsma/XDL/Helpers";
import Search from "@monsma/XDL/Icons/Search";
import chunk from "lodash/chunk";
import find from "lodash/find";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    allBrandsDataView: getAllBrandsDataView(state),
    brandAlphabetState: state.pages.brands.brandAlphabetState,
    location: getLocation(state),
    brandsPageUrl: getPageLinkByPageType(state, "BrandsPage")?.url,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

type BrandLetterMap = {
    [group: string]: BrandModel[];
};

export interface BrandListStyles {
    container?: GridContainerProps;
    mainContentGridItem?: GridItemProps;
    alphabetNavigationGridItem?: GridItemProps;
    actionsSearchWrapper?: InjectableCss;
    actions?: InjectableCss;
    search?: InjectableCss;
    alphabetNavigation?: InjectableCss;
    brandLettersGroup?: InjectableCss;
    brandSectionWrapper?: InjectableCss;
    brandSectionTitle?: TypographyPresentationProps;
    brandSectionGridContainer?: GridContainerProps;
    brandSectionGridItem?: GridItemProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    selectAlphabetNavigationWrapper?: InjectableCss;
}

export const listStyles: BrandListStyles = {
    container: {
        gap: 16,
    },
    mainContentGridItem: {
        width: [12, 12, 12, 10, 10],
        css: css`
            flex-direction: column;
        `,
    },
    alphabetNavigationGridItem: {
        width: [12, 12, 12, 2, 2],
        css: css`
            justify-content: flex-end;
            padding-top: 0;
            padding-bottom: 0;

            ${screen("lt", "lg")} {
                display: none;
            }
        `,
    },
    actionsSearchWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-flow: row wrap;
            align-items: center;
            justify-content: space-between;
            padding-bottom: 20px;
            border-bottom: 1px solid ${getColor("common.ui12")};
            margin-bottom: 40px;
        `,
    },
    search: {
        css: css`
            ${screen("lt", "sm")} {
                width: 100%;
                margin-bottom: 20px;
            }
        `,
    },
    actions: {
        css: css`
            flex: 1;

            ${screen("gt", "md")} {
                > div {
                    flex-direction: row;

                    > div {
                        margin-top: 0 !important;
                        margin-right: 40px;

                        &:nth-child(4) {
                            margin-right: 0;
                        }
                    }
                }
            }

            ${screen("lt", "sm")} {
                width: 100%;
                margin-top: 20px;
                margin-bottom: 20px;
            }
        `,
    },
    selectAlphabetNavigationWrapper: {
        css: css`
            width: 100%;

            ${screen("gte", "sm")} {
                margin-top: 20px;
            }
        `,
    },
    alphabetNavigation: {
        css: css`
            background: ${getColor("common.background")};
            border: 1px solid ${getColor("common.ui12")};
            border-radius: 4px;
            padding: 11px;

            ${screen("gt", "md")} {
                position: sticky;
                top: 180px;
            }
        `,
    },
    brandLettersGroup: {
        css: css`
            font-size: 15px;
            font-weight: 600;
            text-transform: uppercase;
            cursor: pointer;
            transition: all 0.2s linear;
            color: ${getColor("common.ui2")};
            line-height: 26px;
            border-radius: 6px;
            width: 56px;
            height: 45px;
            display: flex;
            align-items: center;
            justify-content: center;

            &.disabled {
                opacity: 0.5;
                cursor: not-allowed;
                pointer-events: none;
            }

            &:hover:not(.disabled) {
                background: ${getColor("common.ui3")};
                color: ${getColor("common.background")};
            }

            &.active {
                background: ${getColor("common.ui4")};
                color: ${getColor("common.background")};
            }
        `,
    },
    brandSectionWrapper: {
        css: css`
            width: 100%;
            margin-bottom: 60px;

            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
    brandSectionTitle: {
        css: css`
            font-weight: 700;
            font-size: 28px;
            line-height: 34px;
            color: ${getColor("secondary.main")};
            text-transform: uppercase;
            border-bottom: 1px solid ${getColor("common.ui12")};
            padding-bottom: 7px;
        `,
    },
    brandSectionGridContainer: {
        gap: 0,
    },
    brandSectionGridItem: {
        width: [12, 6, 6, 4, 4],
        css: css`
            margin-top: 20px !important;
            padding-right: 20px;

            a,
            span {
                font-size: 15px;
                line-height: 26px;
                font-weight: 700;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = listStyles;

const BrandList: FC<Props> = (props: Props) => {
    const { allBrandsDataView, brandAlphabetState, location, brandsPageUrl } = props;
    const history = useHistory();

    const CategoriesList = {
        ALL: translate("All Brands"),
        BUILDING: translate("Building Materials Brands"),
        HEARTH: translate("Hearth Products Brands"),
    };
    const queryParams = parseQueryString<{ category?: string }>(location.search);
    const category = Object.entries(CategoriesList).find(([key]) => key === queryParams.category);

    const brandList = allBrandsDataView.value || [];
    const brandAlphabet = brandAlphabetState.value || [];
    const brandLetterGroupsDetailsTemp = buildBrandLetterGroupsDetails(brandAlphabet);
    const { groups, brandLettersMap } = buildLetterGroupsBrandsMap(brandLetterGroupsDetailsTemp, brandList);
    const brandSections = buildBrandSections(groups, brandLettersMap, undefined, category ? category[0] : "ALL");
    const brandLetterGroupsDetails = buildBrandLetterGroupsDetails(brandAlphabet, brandSections);

    const [filterBrands, setFilterBrands] = useState("");
    const [brandCategory, setBrandCategory] = useState(category ? category[0] : "ALL");
    const [state, setState] = React.useState<any>({ brandSections, brandLetterGroupsDetails });
    const [lastLetterClicked, setlastLetterClicked] = React.useState("");
    const [selectedLettersGroup, setSelectedLettersGroup] = React.useState("");
    const [selectedAlphabetNavigation, setSelectedAlphabetNavigation] = React.useState("");

    useEffect(() => {
        setState({ ...state, brandSections, brandLetterGroupsDetails });
    }, [brandList?.length, brandAlphabetState, brandAlphabet]);

    useEffect(() => {
        const currentCategory = category ? category[0] : "ALL";

        if (currentCategory !== brandCategory) {
            setBrandCategory(currentCategory);
            brandCategoryChangeEvent(currentCategory);
        }
    }, [location.search]);

    React.useLayoutEffect(() => {
        if (lastLetterClicked) {
            const letterElement = document.getElementById(`letter-${lastLetterClicked}`);
            if (letterElement) {
                const letterOffset = letterElement.offsetTop;
                if (letterOffset > 0) {
                    window.scrollTo(0, letterOffset);
                }
            }
            setlastLetterClicked("");
        }
    }, [lastLetterClicked]);

    if (allBrandsDataView.isLoading || brandAlphabetState.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (brandList.length === 0) {
        return null;
    }

    const handleFilterBrands = (e: any) => {
        const target = e.target;
        const value = target.value;

        setFilterBrands(value);

        const brandSections = buildBrandSections(groups, brandLettersMap, value);
        const brandLetterGroupsDetails = buildBrandLetterGroupsDetails(brandAlphabet, brandSections);

        setState({
            ...state,
            brandSections,
            brandLetterGroupsDetails,
        });
    };

    const handleBrandCategoryChange = (e: any) => {
        const value = e.target.value;

        history.push(`${brandsPageUrl}?category=${value || "ALL"}`);
    };

    const brandCategoryChangeEvent = (value: string) => {
        setBrandCategory(value);
        setFilterBrands("");

        const brandSections = buildBrandSections(groups, brandLettersMap, undefined, value);
        const brandLetterGroupsDetails = buildBrandLetterGroupsDetails(brandAlphabet, brandSections);

        setState({
            ...state,
            brandSections,
            brandLetterGroupsDetails,
        });
    };

    const doScroll = (group: string) => {
        const letterElement = document.getElementById(`brandSectionGroup_${group}`);

        if (letterElement) {
            window.scrollTo(0, letterElement.offsetTop - 200);

            setSelectedLettersGroup(group);
        }
    };

    const handleBrandLettersGroupClick = (group: string) => {
        doScroll(group);
    };

    const handleAlphabetNavigationChange = (e: any) => {
        const value = e.target.value;

        setSelectedAlphabetNavigation(value);
        doScroll(value);
    };

    const brandsCount =
        state.brandSections &&
        state.brandSections.length > 0 &&
        state.brandSections.filter((child: any) => {
            return child.brandMap.length > 0;
        }).length > 0;

    const buildAlphabetNavigation = (asSelect?: boolean) => {
        const mappedBrandLetterGroupsDetails = state.brandLetterGroupsDetails.map((brandLettersGroup: any) => {
            if (asSelect) {
                if (!brandLettersGroup.linkable) {
                    return null;
                }

                return (
                    <option key={brandLettersGroup.name} value={brandLettersGroup.name}>
                        {brandLettersGroup.title}
                    </option>
                );
            }

            return (
                <StyledWrapper
                    key={brandLettersGroup.name}
                    {...styles.brandLettersGroup}
                    className={
                        !brandLettersGroup.linkable || filterBrands
                            ? "disabled"
                            : selectedLettersGroup === brandLettersGroup.name
                            ? "active"
                            : ""
                    }
                    onClick={() => {
                        handleBrandLettersGroupClick(brandLettersGroup.name);
                    }}
                >
                    {brandLettersGroup.title}
                </StyledWrapper>
            );
        });

        if (asSelect) {
            return (
                <Select
                    placeholder="Select"
                    value={selectedAlphabetNavigation}
                    onChange={handleAlphabetNavigationChange}
                    disabled={!!filterBrands}
                    css={css`
                        text-transform: uppercase;
                    `}
                >
                    {mappedBrandLetterGroupsDetails}
                </Select>
            );
        }

        return <StyledWrapper {...styles.alphabetNavigation}>{mappedBrandLetterGroupsDetails}</StyledWrapper>;
    };

    return (
        <GridContainer {...styles.container} data-test-selector="brandList">
            <GridItem width={12}>
                <Typography
                    variant="h1"
                    css={css`
                        margin-bottom: 0;
                    `}
                >
                    {(CategoriesList as any)[brandCategory]}
                </Typography>
            </GridItem>
            <GridItem {...styles.mainContentGridItem}>
                <StyledWrapper {...styles.actionsSearchWrapper}>
                    <StyledWrapper {...styles.actions}>
                        <RadioGroup value={brandCategory} onChangeHandler={handleBrandCategoryChange}>
                            {Object.entries(CategoriesList).map(([key, value]) => (
                                <Radio key={key} value={key}>
                                    {value}
                                </Radio>
                            ))}
                        </RadioGroup>
                    </StyledWrapper>
                    <StyledWrapper {...styles.search}>
                        <TextField
                            name="filterBrands"
                            value={filterBrands}
                            iconProps={{
                                src: Search,
                            }}
                            placeholder={translate("Search by Keyword")}
                            onChange={handleFilterBrands}
                        />
                    </StyledWrapper>
                    <Hidden {...styles.selectAlphabetNavigationWrapper} above="md">
                        {buildAlphabetNavigation(true)}
                    </Hidden>
                </StyledWrapper>
                {brandsCount && (
                    <>
                        {state.brandSections.map((brandSection: any) => {
                            if (!brandSection.brandMap.length) {
                                return;
                            }

                            return (
                                <StyledWrapper
                                    key={brandSection.group}
                                    {...styles.brandSectionWrapper}
                                    id={`brandSectionGroup_${brandSection.group}`}
                                >
                                    <Typography as="h2" {...styles.brandSectionTitle}>
                                        {brandSection.group}
                                    </Typography>
                                    <GridContainer {...styles.brandSectionGridContainer}>
                                        {brandSection.brandMap.map((brand: any) => {
                                            return (
                                                <GridItem key={brand.id} {...styles.brandSectionGridItem}>
                                                    <Typography
                                                        variant="h3"
                                                        css={css`
                                                            line-height: 0;
                                                            margin-bottom: 0;
                                                        `}
                                                    >
                                                        <Link href={brand.detailPagePath}>{brand.name}</Link>
                                                    </Typography>
                                                </GridItem>
                                            );
                                        })}
                                    </GridContainer>
                                </StyledWrapper>
                            );
                        })}
                    </>
                )}
                {!brandsCount && (
                    <Notify
                        messageType="danger"
                        body={siteMessage("Custom_BrandList_NoBrandsFound")}
                        hideCloseButton={true}
                    />
                )}
            </GridItem>
            <GridItem {...styles.alphabetNavigationGridItem}>{buildAlphabetNavigation()}</GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(BrandList),
    definition: {
        group: "Brands",
        icon: "List",
        allowedContexts: ["BrandsPage"],
    },
};

export default widgetModule;

const isBrandAlphabetGroupsLinkable = (group: any) => {
    let linkable = false;

    for (let i = 0; i < group.length; i++) {
        const groupItem = group[i];

        if (groupItem.count > 0) {
            linkable = true;

            break;
        }
    }

    return linkable;
};

const buildBrandLetterGroupsDetails = (brandAlphabet: BrandAlphabetLetterModel[], brandSections?: any) => {
    const brandAlphabetLetters: any = [];
    const brandAlphabetNumbers: any = [];

    for (let i = 0; i < brandAlphabet.length; i++) {
        const brandAlphabetItem = brandAlphabet[i];
        const isAlphabetLetter = "abcdefghijklmnopqrstuvwxyz".indexOf(brandAlphabetItem["letter"]) > -1;

        isAlphabetLetter ? brandAlphabetLetters.push(brandAlphabetItem) : brandAlphabetNumbers.push(brandAlphabetItem);
    }

    const brandAlphabetGroups = chunk(brandAlphabetLetters, 4);
    const brandLetterGroupsDetails = brandAlphabetGroups.map((group: any, index: any) => {
        let extendedGroup;

        if (index === brandAlphabetGroups.length - 1) {
            return null;
        }

        if (index === brandAlphabetGroups.length - 2) {
            extendedGroup = group.concat(brandAlphabetGroups[index + 1]);
        }

        const finalGroup = extendedGroup || group;
        const groupName = `${finalGroup[0]["letter"]}-${finalGroup[finalGroup.length - 1]["letter"]}`;

        let linkableGroup = isBrandAlphabetGroupsLinkable(finalGroup);

        if (brandSections) {
            for (let i = 0; i < brandSections.length; i++) {
                const brandSection = brandSections[i];

                if (brandSection["group"] === groupName) {
                    linkableGroup = linkableGroup && brandSection["brandMap"].length > 0;

                    break;
                }
            }
        }

        return {
            linkable: linkableGroup,
            group: finalGroup,
            name: groupName,
            title: `${finalGroup[0]["letter"]} - ${finalGroup[finalGroup.length - 1]["letter"]}`,
        };
    });

    brandLetterGroupsDetails.splice(brandAlphabetGroups.length - 1, 1);
    brandLetterGroupsDetails.unshift({
        linkable: isBrandAlphabetGroupsLinkable(brandAlphabetNumbers),
        group: brandAlphabetNumbers,
        name: `0-9`,
        title: `0 - 9`,
    });

    return brandLetterGroupsDetails;
};

const buildLetterGroupsBrandsMap = (
    letterGroups: any,
    brandList: BrandModel[],
): { groups: any; brandLettersMap: any } => {
    let groups: string[] = [];
    let newGroups: string[] = groups;
    let group: string;

    const brandLettersMap = {} as any;

    for (let i = 0; i < brandList.length; i++) {
        const brand = brandList[i];
        const letter = brand.name[0] ? brand.name[0].toLowerCase() : "";

        if (isNumeric(letter)) {
            group = "0-9";
        } else {
            group = find(letterGroups, (lettersGroup: any) => {
                return find(lettersGroup.group, (group: any) => {
                    return group.letter === letter;
                });
            }).name;
        }

        if (!brandLettersMap[group]) {
            brandLettersMap[group] = [];
        }

        brandLettersMap[group].push(brand);
        newGroups = [...newGroups, group];
    }

    groups = newGroups.filter((group, index, array) => array.indexOf(group) === index);

    if (brandLettersMap["0-9"]) {
        brandLettersMap["0-9"].sort((a: any, b: any) => parseInt(a.name, 10) - parseInt(b.name, 10));
    }

    return { groups, brandLettersMap };
};

const getBrandLettersMap = (
    brandLettersMap: BrandLetterMap,
    group: string,
    search?: string | undefined,
    category?: string | undefined,
) => {
    if (category && ["BUILDING", "HEARTH"].indexOf(category) > -1) {
        const indicator = category === "BUILDING" ? "isBuildingBrand" : "isHearthBrand";

        return brandLettersMap[group].filter(brand => brand.properties[indicator]?.toLocaleLowerCase() === "true");
    }

    if (search) {
        return brandLettersMap[group].filter(brand => (brand.name || "").toLowerCase().includes(search.toLowerCase()));
    }

    return brandLettersMap[group];
};

const buildBrandSections = (groups: string[], brandLettersMap: BrandLetterMap, search?: string, category?: string) => {
    return groups.map(group => ({
        group,
        brandMap: getBrandLettersMap(brandLettersMap, group, search, category),
    }));
};
